import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    BooleanField,
    FunctionField,
    NumberField,
    useRecordContext,
    useTranslate,
    usePermissions,
    useRedirect,
    EditButton,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Paper,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Business,
    Phone,
    Security,
    BatteryChargingFull,
    Info,
    LocalParking,
    Photo,
    Schedule,
    LocationOn,
    ArrowBack,
    Edit as EditIcon,
    CheckCircle,
    Cancel,
    QrCode,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getLocalizedAddressData } from '../../data/hongKongAddressData';

const getStateDisplayName = (stateKey: string, language: string = 'en') => {
    const addressData = getLocalizedAddressData(language);
    for (const region of addressData) {
        const district = region.districts.find((d) => d.key === stateKey);
        if (district) return district.name;
    }
    return stateKey;
};

const getAccessColors = (accessType: string): { bg: string; text: string } => {
    if (accessType === 'public') return { bg: '#E8F5E8', text: '#009A75' };
    if (accessType === 'private') return { bg: '#FFF3E0', text: '#EF6C00' };
    if (accessType === 'restricted') return { bg: '#F3E5F5', text: '#7B1FA2' };
    return { bg: '#F5F5F5', text: '#757575' };
};

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('sites:write:all');

    if (!record) return null;

    const accessColors = getAccessColors(record.accessType);
    const currentLocale = translate('language.current', { _: 'en' });
    const language = currentLocale === 'zh-TW' ? 'zh-TW' : 'en';
    const districtName = record.state ? getStateDisplayName(record.state, language) : record.city;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, sm: 4 },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/sites')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: '1.25rem', sm: '2.125rem' },
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {record.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {districtName}
                            {record.vendorName ? ` • ${record.vendorName}` : ''}
                        </Typography>
                        <Chip
                            label={translate(`resources.sites.accessTypes.${record.accessType}`, { _: record.accessType || '-' })}
                            size="small"
                            sx={{
                                backgroundColor: accessColors.bg,
                                color: accessColors.text,
                                fontWeight: 600,
                            }}
                        />
                        <Chip
                            icon={record.isActive ? <CheckCircle /> : <Cancel />}
                            label={record.isActive ? translate('status.active') : translate('status.inactive')}
                            size="small"
                            sx={{
                                backgroundColor: record.isActive ? '#E8F5E8' : '#FFEBEE',
                                color: record.isActive ? '#009A75' : '#F44336',
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {canEdit && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <EditButton
                        label={translate('ra.action.edit')}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00C853',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: '#00A843',
                                boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

const EditFab = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('sites:write:all');

    if (!record || !canEdit) return null;
    return (
        <Fab
            color="primary"
            onClick={() => navigate(`/sites/${record.id}`)}
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#009A75',
                '&:hover': { backgroundColor: '#007A5E' },
                zIndex: 1100,
            }}
        >
            <EditIcon />
        </Fab>
    );
};

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }} component="div">
            {children}
        </Typography>
    </Box>
);

const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ mr: 2, color: '#00C853', display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
        </Typography>
    </Box>
);

const OperatingHoursSummary = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const oh: any = record.operatingHours || {};
    const days: { key: string; label: string }[] = [
        { key: 'monday', label: translate('resources.sites.operatingHours.days.monday') },
        { key: 'tuesday', label: translate('resources.sites.operatingHours.days.tuesday') },
        { key: 'wednesday', label: translate('resources.sites.operatingHours.days.wednesday') },
        { key: 'thursday', label: translate('resources.sites.operatingHours.days.thursday') },
        { key: 'friday', label: translate('resources.sites.operatingHours.days.friday') },
        { key: 'saturday', label: translate('resources.sites.operatingHours.days.saturday') },
        { key: 'sunday', label: translate('resources.sites.operatingHours.days.sunday') },
    ];

    const formatHours = (h: any): string => {
        if (!h) return translate('resources.sites.operatingHours.status.closed');
        if (h.closed) return translate('resources.sites.operatingHours.status.closed');
        if (h.open && h.close) {
            if (h.open === '00:00' && h.close === '23:59') return translate('resources.sites.operatingHours.status.247');
            return `${h.open} - ${h.close}`;
        }
        return '-';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {days.map((d) => (
                <Box key={d.key} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F0F0F0', py: 0.75 }}>
                    <Typography variant="body2" sx={{ color: '#606060' }}>{d.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatHours(oh[d.key])}</Typography>
                </Box>
            ))}
            {oh.publicHolidays && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography variant="body2" sx={{ color: '#606060' }}>
                        {translate('resources.sites.operatingHours.publicHolidays.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {oh.publicHolidays.closed
                            ? translate('resources.sites.operatingHours.status.closed')
                            : oh.publicHolidays.open && oh.publicHolidays.close
                                ? `${oh.publicHolidays.open} - ${oh.publicHolidays.close}`
                                : '-'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const ImagesSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const primary: string | undefined = record.primaryImage;
    const gallery: any[] = Array.isArray(record.images) ? record.images : [];
    const allImages: { url: string; caption?: string; primary?: boolean }[] = [];
    if (primary) allImages.push({ url: primary, primary: true });
    gallery.forEach((img: any) => {
        if (typeof img === 'string') allImages.push({ url: img });
        else if (img?.url) allImages.push({ url: img.url, caption: img.caption });
    });

    if (allImages.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('resources.sites.fields.images')}: -
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 1.5,
            }}
        >
            {allImages.map((img, idx) => (
                <Box
                    key={`${img.url}-${idx}`}
                    sx={{
                        position: 'relative',
                        paddingTop: '75%',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #E0E0E0',
                        backgroundColor: '#FAFAFA',
                    }}
                >
                    <Box
                        component="img"
                        src={img.url}
                        alt={img.caption || `image-${idx}`}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                    {img.primary && (
                        <Chip
                            label={translate('resources.sites.fields.primaryImage')}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                backgroundColor: '#00C853',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                height: 22,
                            }}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
};

export const SitesShow = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    px: { xs: 1, sm: 2 },
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
                    <PageHeader />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 0, sm: 3 } }}>
                        {/* Left Column */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 600px' } }}>
                            {/* Basic Information */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<Business />} title={translate('resources.sites.edit.basicInfo')} />

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.premisesName')}>
                                                <TextField source="name" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.merchant')}>
                                                <TextField source="vendorName" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.timezone')}>
                                                <TextField source="timezone" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Address */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<LocationOn />} title={translate('resources.sites.fields.address')} />

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: '1 1 100%' }}>
                                            <FieldRow label={translate('resources.sites.fields.addressLine1')}>
                                                <TextField source="addressLine1" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: '1 1 100%' }}>
                                            <FieldRow label={translate('resources.sites.fields.addressLine2')}>
                                                <TextField source="addressLine2" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.country')}>
                                                <TextField source="country" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.city')}>
                                                <TextField source="city" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.district')}>
                                                <FunctionField
                                                    render={(rec: any) => {
                                                        const currentLocale = translate('language.current', { _: 'en' });
                                                        const language = currentLocale === 'zh-TW' ? 'zh-TW' : 'en';
                                                        const name = rec.state ? getStateDisplayName(rec.state, language) : rec.city;
                                                        return name || '-';
                                                    }}
                                                />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.postalCode')}>
                                                <TextField source="postalCode" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.latitude')}>
                                                <NumberField source="latitude" options={{ maximumFractionDigits: 6 }} />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.longitude')}>
                                                <NumberField source="longitude" options={{ maximumFractionDigits: 6 }} />
                                            </FieldRow>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Contact Information */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<Phone />} title={translate('resources.sites.edit.contactInfo')} />

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.phone')}>
                                                <TextField source="phone" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.email')}>
                                                <TextField source="email" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.supportPhone')}>
                                                <TextField source="supportPhone" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                                            <FieldRow label={translate('resources.sites.fields.website')}>
                                                <TextField source="website" emptyText="-" />
                                            </FieldRow>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Charger Information */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<BatteryChargingFull />} title={translate('resources.sites.edit.chargerInfo')} />

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#E8F5E8' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#00C853', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <NumberField source="chargerCount" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.sites.fields.chargerCount')}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#E3F2FD' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#2196F3', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <NumberField source="availableChargers" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.sites.fields.availableChargers')}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#FFF3E0' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#FF9800', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <NumberField source="totalMaxPower" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.sites.fields.totalMaxPower')}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                                            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#F3E5F5' }}>
                                                <Typography sx={{ fontWeight: 600, color: '#9C27B0', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                                    <NumberField source="gridConnectionCapacity" />
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('resources.sites.fields.gridConnectionCapacity')}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>

                        {/* Right Column */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' } }}>
                            {/* Access & Status */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<Security />} title={translate('resources.sites.edit.accessStatus')} />

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FieldRow label={translate('resources.sites.fields.accessType')}>
                                            <FunctionField
                                                render={(rec: any) => {
                                                    const colors = getAccessColors(rec.accessType);
                                                    return (
                                                        <Chip
                                                            label={translate(`resources.sites.accessTypes.${rec.accessType}`, { _: rec.accessType || '-' })}
                                                            size="small"
                                                            sx={{ backgroundColor: colors.bg, color: colors.text, fontWeight: 600 }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.isActive')}>
                                            <BooleanField
                                                source="isActive"
                                                valueLabelTrue={translate('status.active')}
                                                valueLabelFalse={translate('status.inactive')}
                                                TrueIcon={CheckCircle}
                                                FalseIcon={Cancel}
                                                sx={{
                                                    '& .RaBooleanField-true': { color: '#009A75' },
                                                    '& .RaBooleanField-false': { color: '#F44336' },
                                                }}
                                            />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.operatingAccess')}>
                                            <FunctionField
                                                render={(rec: any) =>
                                                    rec.operatingAccess
                                                        ? translate(`resources.sites.operatingAccess.${rec.operatingAccess}`, { _: rec.operatingAccess })
                                                        : '-'
                                                }
                                            />
                                        </FieldRow>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Features */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<LocalParking />} title={translate('resources.sites.edit.features')} />

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FieldRow label={translate('resources.sites.fields.parkingType')}>
                                            <FunctionField
                                                render={(rec: any) =>
                                                    rec.parkingType
                                                        ? translate(`resources.sites.parkingTypes.${rec.parkingType}`, { _: rec.parkingType })
                                                        : '-'
                                                }
                                            />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.hasCanopy')}>
                                            <BooleanField
                                                source="hasCanopy"
                                                TrueIcon={CheckCircle}
                                                FalseIcon={Cancel}
                                                sx={{
                                                    '& .RaBooleanField-true': { color: '#009A75' },
                                                    '& .RaBooleanField-false': { color: '#909090' },
                                                }}
                                            />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.reservationEnabled')}>
                                            <BooleanField
                                                source="reservationEnabled"
                                                TrueIcon={CheckCircle}
                                                FalseIcon={Cancel}
                                                sx={{
                                                    '& .RaBooleanField-true': { color: '#009A75' },
                                                    '& .RaBooleanField-false': { color: '#909090' },
                                                }}
                                            />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.emailReceiptEnabled')}>
                                            <BooleanField
                                                source="emailReceiptEnabled"
                                                TrueIcon={CheckCircle}
                                                FalseIcon={Cancel}
                                                sx={{
                                                    '& .RaBooleanField-true': { color: '#009A75' },
                                                    '& .RaBooleanField-false': { color: '#909090' },
                                                }}
                                            />
                                        </FieldRow>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* QR Code */}
                            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <SectionHeader icon={<QrCode />} title={translate('resources.sites.fields.qrCode')} />
                                    <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                        <TextField source="qrCode" emptyText="-" />
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>

                    {/* Operating Hours - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <SectionHeader icon={<Schedule />} title={translate('resources.sites.operatingHours.title')} />
                            <OperatingHoursSummary />
                        </CardContent>
                    </Card>

                    {/* Parking & Restrictions - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <SectionHeader icon={<LocalParking />} title={translate('resources.sites.edit.parkingRestrictions')} />

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.parkingInfo')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <FieldRow label={translate('resources.sites.fields.parkingWeekdayRate')}>
                                            <NumberField source="parkingInfo.weekdayRate" emptyText="-" />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.parkingWeekendRate')}>
                                            <NumberField source="parkingInfo.weekendRate" emptyText="-" />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.parkingPublicHolidayRate')}>
                                            <NumberField source="parkingInfo.publicHolidayRate" emptyText="-" />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.parkingFreeMinutes')}>
                                            <NumberField source="parkingInfo.freeMinutes" emptyText="-" />
                                        </FieldRow>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.restrictions')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <FieldRow label={translate('resources.sites.fields.heightLimit')}>
                                            <TextField source="restrictions.heightLimit" emptyText="-" />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.security')}>
                                            <TextField source="restrictions.security" emptyText="-" />
                                        </FieldRow>
                                        <FieldRow label={translate('resources.sites.fields.restrictionNotes')}>
                                            <TextField source="restrictions.notes" emptyText="-" />
                                        </FieldRow>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Images - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <SectionHeader icon={<Photo />} title={translate('resources.sites.fields.images')} />
                            <ImagesSection />
                        </CardContent>
                    </Card>

                    {/* Notes - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <SectionHeader icon={<Info />} title={translate('resources.sites.fields.notes')} />
                            <FunctionField
                                render={(rec: any) =>
                                    rec.notes ? (
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {rec.notes}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )
                                }
                            />
                        </CardContent>
                    </Card>

                    {/* Pricing Info - Full Width */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <SectionHeader icon={<Info />} title={translate('resources.sites.fields.pricingInfo')} />
                            <FunctionField
                                render={(rec: any) =>
                                    rec.pricingInfo ? (
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {rec.pricingInfo}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )
                                }
                            />
                        </CardContent>
                    </Card>

                    {/* Metadata */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                {translate('resources.chargers.show.metadata', { _: 'Metadata' })}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
                                <FieldRow label={translate('resources.sites.fields.createdAt')}>
                                    <DateField source="createdAt" showTime />
                                </FieldRow>
                                <FieldRow label={translate('resources.sites.fields.updatedAt')}>
                                    <DateField source="updatedAt" showTime />
                                </FieldRow>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleShowLayout>
            {isMobile && <EditFab />}
        </Show>
    );
};
